import React from 'react';
import { View, Text, Pressable, StyleSheet, ViewStyle, TextStyle } from 'react-native';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';

interface StepHeaderProps {
  title: string;
  subtitle?: string;
}

/**
 * StepHeader - Header component for wizard steps
 */
export const StepHeader: React.FC<StepHeaderProps> = ({ title, subtitle }) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();

  return (
    <View style={styles.headerContainer}>
      <Text style={[styles.headerTitle, { color: MARKETPLACE_COLORS.textPrimary }]}>{title}</Text>
      {subtitle && <Text style={[styles.headerSubtitle, { color: MARKETPLACE_COLORS.textSecondary }]}>{subtitle}</Text>}
    </View>
  );
};

/**
 * Shared styles for maintenance wizard steps
 */
export const maintenanceStepStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingVertical: 0,
    gap: 12,
  },

  // Selection items (radio options)
  selectionItem: {
    borderRadius: 8,
    paddingHorizontal: 20,
    paddingVertical: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  selectionItemExpanded: {
    borderRadius: 8,
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 20,
    gap: 16,
  },
  selectionItemText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 21,
  },

  // Category cards (with icon and description)
  categoryCard: {
    borderRadius: 8,
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 20,
    flexDirection: 'row',
    gap: 16,
  },
  categoryIconContainer: {
    width: 46,
    height: 46,
    borderRadius: 23,
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoryContentContainer: {
    flex: 1,
    gap: 16,
  },
  categoryHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  categoryTitle: {
    flex: 1,
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 21,
  },
  categoryDescription: {
    fontSize: 12,
    fontWeight: '300',
    lineHeight: 18,
  },

  // Radio buttons
  radioContainer: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioOuter: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioOuterSelected: {
  },
  radioInner: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },

  // Toggle bar
  toggleBar: {
    borderRadius: 8,
    padding: 2,
    flexDirection: 'row',
  },
  toggleOption: {
    flex: 1,
    height: 30,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleOptionActive: {
  },
  toggleOptionText: {
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 18,
  },
  toggleOptionTextActive: {
  },

  // Input fields
  inputContainer: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 16,
  },
  inputLabel: {
    fontSize: 10,
    fontWeight: '600',
    lineHeight: 12.5,
    marginTop: 4,
  },
  inputText: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 17.5,
    paddingVertical: 4,
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
    paddingTop: 11,
    paddingBottom: 11,
  },

  // Dropdown/Select
  selectContainer: {
    borderRadius: 8,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    paddingHorizontal: 12,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
  },
  selectText: {
    flex: 1,
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
  },
  selectTextFilled: {
  },

  // Alert box
  alertBox: {
    backgroundColor: 'rgba(255, 118, 41, 0.1)',
    borderRadius: 8,
    padding: 16,
    flexDirection: 'row',
    gap: 10,
  },
  alertText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '400',
    color: '#FF7629',
    lineHeight: 21,
  },

  // Phone number row
  phoneRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  phoneIconContainer: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  phoneNumber: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },

  // Date/time field
  dateTimeField: {
    borderRadius: 8,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderBottomWidth: 1,
  },
  dateTimeLabel: {
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 12,
    letterSpacing: 0.15,
  },
  dateTimeValue: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
  },
  dateTimePlaceholder: {
  },
});

const styles = StyleSheet.create({
  headerContainer: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 20,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    lineHeight: 30,
  },
  headerSubtitle: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 21,
    marginTop: 4,
  },
});

/**
 * RadioButton component
 */
interface RadioButtonProps {
  selected: boolean;
}

export const RadioButton: React.FC<RadioButtonProps> = ({ selected }) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();

  return (
    <View style={maintenanceStepStyles.radioContainer}>
      <View style={[
        maintenanceStepStyles.radioOuter,
        { borderColor: MARKETPLACE_COLORS.inputBorder, backgroundColor: MARKETPLACE_COLORS.inputBackground },
        selected && {
          backgroundColor: MARKETPLACE_COLORS.checkboxSelected,
          borderColor: MARKETPLACE_COLORS.checkboxSelected,
        },
      ]}>
        {selected && <View style={[maintenanceStepStyles.radioInner, { backgroundColor: MARKETPLACE_COLORS.textPrimary }]} />}
      </View>
    </View>
  );
};

/**
 * ToggleBar component for Yes/No selection
 */
interface ToggleBarProps {
  options: string[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}

export const ToggleBar: React.FC<ToggleBarProps> = ({ options, selectedIndex, onSelect }) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();

  return (
    <View style={[maintenanceStepStyles.toggleBar, { backgroundColor: MARKETPLACE_COLORS.uploadAreaBackground }]}>
      {options.map((option, index) => (
        <Pressable
          key={option}
          style={[
            maintenanceStepStyles.toggleOption,
            selectedIndex === index && { backgroundColor: MARKETPLACE_COLORS.inputBackground },
          ]}
          onPress={() => onSelect(index)}
        >
          <Text style={[
            maintenanceStepStyles.toggleOptionText,
            { color: MARKETPLACE_COLORS.textSecondary },
            selectedIndex === index && { color: MARKETPLACE_COLORS.textPrimary },
          ]}>
            {option}
          </Text>
        </Pressable>
      ))}
    </View>
  );
};
